<template>
  <div class="heatmap-report"
       v-if="applyData">
    <heatmap :mode="'only'"
             :aid="aid"
             :captureAid="captureAid"
             :abMode="abMode"
             :abTestType="abTestType"
             :infoData="infoData"
             :activeSelector="activeSelector"
             :userActionData="userActionData"
             :conversion="initialConversion"
             :activityStatistics="initialActivityStatistics"
             :initialReferer="initialReferer"
             :initialDevice="initialDevice"
             :initialEventSource="initialEventSource"
             :initialEventType="initialEventType"
             :initialHeatmapType="initialHeatmapType"
             :initialCampaign="initialCampaign"
             :initialUtm="initialUtm"
             :initialUtid="initialUtid"
             @onHeatmapLoadEnd="onHeatmapLoadEnd"
             @onHeatmapData="onHeatmapData"
             @onInfoData="onInfoData" />
  </div>
</template>

<script>
import store from '@/store'
import { parse, format, addDays, isBefore } from 'date-fns'
import { mapMutations, mapState } from 'vuex'
import { getOffset, parseJwt } from '@/libs/utils'
import { getAuthToken } from '@/libs/auth-helper'
import { DOCUMENT_TITLE_PREFIX } from '@/libs/constants'
import { getApply, getApplyByAbTest } from '@/api/apply'
import { getReportPeriod, getReportPeriodByAbTest } from '@/api/report'
import { getDialog } from '@/libs/vue-utils'
import { changeLocale } from '@/libs/i18n'

import Heatmap from '@/components/heatmap'

const openDialog = getDialog('dialogType')

function loadApplyData (aid, query, abTestType) {
  const applyApi = abTestType !== 'text' ? getApply(aid) : getApplyByAbTest(aid)
  const periodApi = abTestType !== 'text' ? getReportPeriod(aid) : getReportPeriodByAbTest(aid)

  return Promise.all([
    applyApi,
    periodApi
  ]).then(result => {
    const apply = result[0]
    const period = result[1]

    if (!apply || !apply.meta.canViewReport) {
      return null
    }

    const tokenData = parseJwt(getAuthToken())
    const userId = tokenData.identity.user_id
    const userNum = tokenData.identity.user_num
    const isAdmin = tokenData.identity.is_admin

    if (userId !== 'admin' && !isAdmin && !apply.meta.shared) {
      if (userId !== apply.userId || userNum !== apply.userNum) {
        return null
      }
    }

    const dateFormat = 'YYYY-MM-DD'
    const minDate = parse(period.minDate)
    const maxDate = parse(period.maxDate)
    let startDate = addDays(maxDate, -6)
    let endDate

    if (isBefore(startDate, minDate)) {
      startDate = parse(minDate)
    }

    if (query.startDate) {
      startDate = parse(query.startDate)
    }

    if (query.endDate) {
      endDate = parse(query.endDate)
    }

    apply.timezone = getOffset(apply.timezoneName)

    store.commit('setApplyData', apply)
    store.commit('setReportPeriod', {
      start: format(startDate, dateFormat),
      end: format(endDate || maxDate, dateFormat),
      min: format(minDate, dateFormat),
      max: format(maxDate, dateFormat)
    })
    document.title = `${DOCUMENT_TITLE_PREFIX} - Report(${apply.pageName})`
    return apply
  })
}

export default {
  name: 'HeatmapReport',
  components: {
    Heatmap
  },
  props: [
    'cacheManager',
    'isAMP'
  ],
  data () {
    return {
      reportData: null,
      reportName: this.$route.query.eventType || this.paramData.eventType || 'click',
      metric: null,
      activeSelector: null,
      userActionData: null,
      infoData: null
    }
  },
  computed: {
    ...mapState([
      'applyData'
    ]),
    aid () {
      return this.$route.params.aid
    },
    captureAid () {
      if (this.abMode === 'bType') {
        return this.aid
      }
      return this.$route.params.abAid
    },
    abTestType () {
      return this.$route.query.abTestType || null
    },
    abMode () {
      return ['aType', 'bType'].includes(this.$route.query.mode) ? this.$route.query.mode : false
    },
    initialDevice () {
      return this.$route.query.device || this.paramData.device || 'desktop'
    },
    initialReferer () {
      return this.$route.query.referer || this.paramData.referer || 'all'
    },
    initialEventSource () {
      return this.$route.query.eventSource || this.paramData.eventSource || 'all'
    },
    initialEventType () {
      return this.$route.query.eventType || this.paramData.eventType || 'click'
    },
    initialHeatmapType () {
      return Number(this.$route.query.heatmapType) || Number(this.paramData.heatmapType) || 0
    },
    initialConversion () {
      return this.paramData.conversion !== 'null' ? this.paramData.conversion : null
    },
    initialActivityStatistics () {
      return this.reportName !== 'stream' ? 'median' : this.$route.query.activityStatistics || this.paramData.activityStatistics || 'median'
    },
    initialCampaign () {
      if (this.paramData.campaign) { return this.paramData.campaign !== 'null' ? this.paramData.campaign : null }
      return this.$route.query.campaign && this.$route.query.campaign !== 'null' ? JSON.parse(this.$route.query.campaign) : null
    },
    initialUtm () {
      if (this.paramData.utm) { return this.paramData.utm !== 'null' ? this.paramData.utm : null }
      return this.$route.query.utm && this.$route.query.utm !== 'null' ? JSON.parse(this.$route.query.utm) : null
    },
    initialUtid () {
      if (this.paramData.utid) { return this.paramData.utid !== 'null' ? this.paramData.utid : null }
      return this.$route.query.utid && this.$route.query.utid !== 'null' ? this.$route.query.utid : null
    },
    ctaData () {
      let data = null
      if (this.$route.query.ctaData) {
        data = JSON.parse(this.$route.query.ctaData)
      } else if (this.paramData.ctaData) {
        data = this.paramData.ctaData
      }
      if (!data || !data.coords) {
        return null
      }
      if (!data.except) {
        data.except = false
      }
      return data
    },
    reportComponent () {
      switch (this.reportName) {
        case 'click':
          return 'ReportClick'
        case 'tap':
          return 'ReportTap'
        case 'zoom':
          return 'ReportZoom'
        case 'move':
          return 'ReportMove'
        case 'swipe':
          return 'ReportSwipe'
        case 'scroll':
          return 'ReportScroll'
        case 'stream':
          return 'ReportStream'
      }

      if (this.isAMP) {
        switch (this.reportName) {
          case 'click':
          case 'tap':
            return 'ReportClick'
          case 'zoom':
          case 'move':
            return 'ReportMove'
          case 'scroll':
            return 'ReportScroll'
          case 'stream':
            return 'ReportStream'
        }
      }
      return null
    }
  },
  beforeCreate () {
    this.paramData = JSON.parse(localStorage.getItem('paramData')) || {}
  },
  created () {
    if (this.ctaData) {
      this.setCtaData(this.ctaData)
    }

    const lang = this.$route.query.lang || this.paramData.lang || 'ko'
    if (this.$i18n !== lang) {
      changeLocale(this.$i18n, lang)
    }
  },
  beforeRouteEnter (to, from, next) {
    const aid = to.params.aid
    const abAid = to.params.abAid
    const paramData = JSON.parse(localStorage.getItem('paramData')) || {}
    if (!aid) {
      openDialog('report.invalidRequest').then(() => {
        return next({ name: 'dashboard' })
      })
    } else {
      to.query.startDate = to.query.startDate || paramData.startDate
      to.query.endDate = to.query.endDate || paramData.endDate
      if (to.query.mode && ['aType', 'bType'].includes(to.query.mode)) {
        to.query.bab = true
      }
      const abTestType = to.query.abTestType || paramData.abTestType
      const startDate = to.query.startDate || new Date()
      const endDate = to.query.endDate || addDays(startDate, -7)
      store.commit('setReportPeriod', {
        start: format(startDate, 'YYYY-MM-DD'),
        end: format(endDate, 'YYYY-MM-DD')
      })

      store.commit('setTimeseriesData', paramData.timeSeriesData)

      loadApplyData(abAid || aid, to.query, abTestType).then(result => {
        if (!result) {
          openDialog('report.invalidRequest').then(() => {
            return next({ name: 'dashboard' })
          })
        } else {
          return next()
        }
      }).catch(err => {
        console.error(err)
        return next({ name: 'dashboard' })
      })
    }
  },
  methods: {
    ...mapMutations([
      'setCtaData'
    ]),
    onHeatmapData (key, value) {
      this.reportName = key
      this.reportData = value
    },
    onInfoData (value) {
      this.infoData = value
    },
    nullOrBool (value) {
      if (value === 'null') {
        return null
      } else if (value === 'true') {
        return true
      } else if (value === 'false') {
        return false
      } else {
        return Boolean(Number(value))
      }
    },
    onHeatmapLoadEnd () {
      setTimeout(() => {
        window.status = 'ready'
      }, 200)
    }
  }
}
</script>

<style lang="scss" scoped>
.heatmap-report {
  & /deep/ .coords_rect_except_info {
    display: none;
  }
}
</style>
